/**
 * Native app update gate.
 *
 * On launch (native shells only) we read the installed build number from
 * Capacitor's App.getInfo() and compare it to the thresholds served by
 * GET /app/version-gate:
 *
 *   installed < minBuild    → "forced"   (hard, non-dismissible block)
 *   installed < latestBuild → "optional" (soft, dismissible nudge)
 *   otherwise               → "ok"
 *
 * Build number is authoritative (iOS CFBundleVersion == Android
 * versionCode). On the web / PWA this is always "ok" — there's nothing to
 * update from a store, the SW handles bundle refreshes.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet } from "@/api/client";
import { isNative, getPlatform } from "@/lib/platform";
import { isVersionBelow } from "@/lib/version-compare";

export type UpdateStatus = "ok" | "optional" | "forced";

interface PlatformGate {
  minBuild: number;
  latestBuild: number;
  latestVersion: string | null;
  storeUrl: string | null;
  // iOS-only, additive: the marketing-version force floor used when the
  // server has no App Store Connect credentials configured and so no real
  // build number for iOS (see apps/api's app-release-sync.ts). Always
  // absent/null for Android, and null for iOS whenever the build-based
  // path (minBuild) is the one active.
  minVersion?: string | null;
}

// Nested per platform (see apps/api routes/app-version) — an iOS force must
// never read Android's numbers or vice versa. `ios`/`android` are additive:
// they don't exist on whatever an old server build might still return, so
// every read below falls back to the legacy flat fields when they're
// missing rather than assuming they're present.
interface VersionGate {
  minBuild: number;
  latestBuild: number;
  latestVersion: string | null;
  storeUrl: { android: string; ios: string | null };
  ios?: PlatformGate;
  android?: PlatformGate;
}

export interface AppUpdateState {
  status: UpdateStatus;
  /** Store URL for the current platform (or a search fallback on iOS). */
  storeUrl: string;
  latestVersion: string | null;
  installedBuild: number | null;
  /** Dismiss the soft "optional" nudge for this session. No-op when forced. */
  dismiss: () => void;
}

/**
 * CampusHubAI is not on the App Store yet — iOS ships through TestFlight, which
 * prompts for its own builds. So there is nothing for our update gate to send an
 * iOS user to: the CTA used to fall back to an App Store *search* page, and a
 * forced gate would have hard-blocked a TestFlight tester with no way forward.
 *
 * The switch is the server's `storeUrl.ios`, which is empty until
 * `APP_STORE_URL_IOS` is set (see lib/env.ts). No listing → no gate on iOS. The
 * day the listing goes live, setting that one env var turns the gate back on
 * with no client change and no release.
 */
function iosGateReady(iosStoreUrl: string | null): boolean {
  return !!iosStoreUrl && iosStoreUrl.trim().length > 0;
}

// Imported exactly once and reused by every call site below — two separate
// `await import("@capacitor/app")` calls from the same module have been
// observed to resolve inconsistently under test mocking, so there is a
// single cached module promise instead.
let capacitorAppImport: Promise<typeof import("@capacitor/app")> | null = null;
function getCapacitorApp(): Promise<typeof import("@capacitor/app")> {
  capacitorAppImport ??= import("@capacitor/app");
  return capacitorAppImport;
}

interface InstalledInfo {
  build: number | null;
  /** Marketing version, e.g. "2.0.73" — used only for the iOS
   *  marketing-version fallback (`minVersion`) comparison below. */
  version: string | null;
}

async function readInstalledInfo(): Promise<InstalledInfo> {
  try {
    const { App } = await getCapacitorApp();
    const info = await App.getInfo();
    const n = parseInt(String(info.build), 10);
    return { build: Number.isFinite(n) ? n : null, version: info.version ?? null };
  } catch {
    return { build: null, version: null };
  }
}

export function useAppUpdate(): AppUpdateState {
  const [status, setStatus] = useState<UpdateStatus>("ok");
  const [storeUrl, setStoreUrl] = useState<string>("");
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [installedBuild, setInstalledBuild] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const checkingRef = useRef(false);

  const check = useCallback(async () => {
    // Only native shells have a store binary to update. Bail on web/PWA.
    if (!isNative()) return;
    // Re-entrancy guard: a resume event firing while the launch check is
    // still in flight (cold-start-then-immediate-resume) must not race two
    // setState sequences against each other.
    if (checkingRef.current) return;
    checkingRef.current = true;
    try {
      const platform = getPlatform();
      const [installed, gate] = await Promise.all([
        readInstalledInfo(),
        // `?platform=` lets the server resolve the flat legacy fields for
        // OUR platform too (see routes/app-version) — harmless since we
        // read the nested object below, but keeps the flat fields accurate
        // for any other consumer of this same response.
        apiGet<VersionGate>(`/app/version-gate?platform=${platform}`).catch(() => null),
      ]);
      const { build } = installed;
      if (!gate || build === null) return;

      // Nested per-platform object is additive; fall back to the flat
      // fields (the shape every already-installed build understands) if a
      // server were ever rolled back to before they existed.
      const platformGate: PlatformGate = platform === "ios"
        ? (gate.ios ?? { minBuild: gate.minBuild, latestBuild: gate.latestBuild, latestVersion: gate.latestVersion, storeUrl: gate.storeUrl.ios })
        : (gate.android ?? { minBuild: gate.minBuild, latestBuild: gate.latestBuild, latestVersion: gate.latestVersion, storeUrl: gate.storeUrl.android });
      // iOS with no store listing configured: stay silent. Leave status at "ok"
      // so neither the forced screen nor the banner can render, and TestFlight
      // remains the only thing telling a tester about a new build.
      if (platform === "ios" && !iosGateReady(platformGate.storeUrl)) {
        setInstalledBuild(build);
        return;
      }
      const url = platformGate.storeUrl!;

      setInstalledBuild(build);
      setStoreUrl(url);
      setLatestVersion(platformGate.latestVersion);
      // iOS marketing-version fallback: only meaningful when the server has
      // no real build number for iOS (minVersion set, ASC creds absent).
      // isVersionBelow fails open (returns false) on any unparsable string,
      // so a bad/missing installed version or minVersion never forces.
      const forcedByVersion = platform === "ios" && !!platformGate.minVersion && !!installed.version
        && isVersionBelow(installed.version, platformGate.minVersion);
      if (build < platformGate.minBuild || forcedByVersion) setStatus("forced");
      else if (build < platformGate.latestBuild) setStatus("optional");
      else setStatus("ok");
    } finally {
      checkingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isNative()) return;
    void check();

    // Re-check on resume: the automatic sync (lib/app-release-sync.ts on
    // the API) can raise minBuild at any time, including while a device
    // sat backgrounded — a user who launched on a fine build and walked
    // away must still get forced the moment they come back, not only on
    // the next cold start.
    let handle: { remove: () => void } | undefined;
    (async () => {
      try {
        const { App } = await getCapacitorApp();
        handle = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) void check();
        });
      } catch {
        // Capacitor unavailable — nothing to listen to.
      }
    })();

    return () => {
      handle?.remove();
    };
  }, [check]);

  return {
    // A dismissed optional nudge collapses back to "ok" for the session.
    status: status === "optional" && dismissed ? "ok" : status,
    storeUrl,
    latestVersion,
    installedBuild,
    dismiss: () => setDismissed(true),
  };
}

/** Open the store listing. Uses `_system` so Capacitor hands off to the
 *  Play Store / App Store app (or the system browser) instead of the
 *  in-app WebView. */
export function openStore(url: string): void {
  if (!url) return;
  window.open(url, "_system");
}
